import React, { useState, useEffect } from 'react';
import { GoogleMap, LoadScript, InfoWindow } from '@react-google-maps/api';
import { Container, Typography, Paper, Box, Alert } from '@mui/material';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import ReviewSection from '../components/ReviewSection';

interface Location {
  _id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  difficulty: string;
  amenities: string[];
  parkingInfo: string;
  accessPoints: {
    name: string;
    latitude: number;
    longitude: number;
  }[];
}

interface LocationState {
  selectedLocationId?: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Map configuration
const mapOptions = {
  mapId: process.env.REACT_APP_GOOGLE_MAPS_ID || 'default',
  disableDefaultUI: false,
  clickableIcons: true,
  scrollwheel: true,
};

const Map: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [error, setError] = useState<string>('');
  const [mapError, setMapError] = useState<string>('');
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const location = useLocation();

  const mapContainerStyle = {
    width: '100%',
    height: '80vh',
  };

  const center = {
    lat: 39.1582, // Delaware's approximate center
    lng: -75.5244,
  };

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        console.log('Fetching locations from:', `${API_URL}/api/locations`);
        const response = await axios.get(`${API_URL}/api/locations`);
        console.log('Locations received:', response.data);
        setLocations(response.data);
      } catch (error) {
        console.error('Error fetching locations:', error);
        setError('Failed to load locations. Please try again later.');
      }
    };

    fetchLocations();
  }, []);

  useEffect(() => {
    // Handle location selection from Locations page
    const locationState = location.state as LocationState;
    if (locationState?.selectedLocationId) {
      const selectedLoc = locations.find(loc => loc._id === locationState.selectedLocationId);
      if (selectedLoc) {
        setSelectedLocation(selectedLoc);
        // Center map on selected location
        if (map) {
          map.panTo({ lat: selectedLoc.latitude, lng: selectedLoc.longitude });
          map.setZoom(12);
        }
      }
    }
  }, [location.state, locations, map]);

  const handleMapError = (error: Error) => {
    console.error('Google Maps Error:', error);
    setMapError('Failed to load Google Maps. Please check your API key and try again.');
  };

  const onMapLoad = (map: google.maps.Map) => {
    setMap(map);
    console.log('Map loaded successfully');
  };

  useEffect(() => {
    if (!map || !locations.length) return;

    // Clear existing markers
    const markers: google.maps.marker.AdvancedMarkerElement[] = [];
    
    // Add new markers
    locations.forEach((location) => {
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat: location.latitude, lng: location.longitude },
        title: location.name,
      });

      marker.addListener('click', () => {
        console.log('Marker clicked:', location);
        setSelectedLocation(location);
      });

      markers.push(marker);
    });

    // Cleanup function
    return () => {
      markers.forEach(marker => marker.map = null);
    };
  }, [map, locations]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Delaware Kayaking Map
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {mapError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mapError}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
        <LoadScript 
          googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
          onError={handleMapError}
          libraries={['marker']}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={8}
            onLoad={onMapLoad}
            options={mapOptions}
          >
            {selectedLocation && (
              <InfoWindow
                position={{ 
                  lat: selectedLocation.latitude, 
                  lng: selectedLocation.longitude 
                }}
                onCloseClick={() => setSelectedLocation(null)}
              >
                <Box sx={{ p: 1, maxWidth: 400 }}>
                  <Typography variant="h6">{selectedLocation.name}</Typography>
                  <Typography variant="body2">{selectedLocation.description}</Typography>
                  <Typography variant="body2">
                    Difficulty: {selectedLocation.difficulty}
                  </Typography>
                  <Typography variant="body2">
                    Parking: {selectedLocation.parkingInfo}
                  </Typography>
                  {selectedLocation.amenities && selectedLocation.amenities.length > 0 && (
                    <Typography variant="body2">
                      Amenities: {selectedLocation.amenities.join(', ')}
                    </Typography>
                  )}
                  <ReviewSection locationId={selectedLocation._id} />
                </Box>
              </InfoWindow>
            )}
          </GoogleMap>
        </LoadScript>
      </Paper>
    </Container>
  );
};

export default Map; 